import { useEffect, useMemo, useRef, useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { PublicQuote, QuoteResponse } from "@forgepoint/shared";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { useRecordQuotesStore } from "@/modules/quotes/quotesStore";
import { useOrganizationStore } from "@/store/organizationStore";
import { DocumentEditorPanes } from "./DocumentEditorPanes";
import { DocumentEditorToolbar } from "./DocumentEditorToolbar";
import { DocumentPreviewScreen } from "./DocumentPreviewScreen";
import { SampleDataSheet } from "./SampleDataSheet";
import { buildSampleQuote, PLACEHOLDER_QUOTE } from "./sampleQuote";
import { TemplateNameField } from "./TemplateNameField";
import {
  selectBinding,
  selectSample,
  useTemplateSampleStore,
  type SampleBinding,
} from "./templateSampleStore";
import type { DocumentAssetUrls } from "./types";
import { useConfiguredSampleValues } from "./useConfiguredSampleValues";
import { useDocumentEditor } from "./useDocumentEditor";
import { useQuoteDocumentTemplatesStore } from "./quoteDocumentTemplatesStore";

/** Banner explaining why the canvas shows stubs instead of quote data. */
const PLACEHOLDER_NOTICE =
  "Templates are not tied to a quote, so every bound value shows as a ⟨stub⟩ naming the field it will pull. Open Sample to fill them in or preview with a real quote.";

/** Banner shown while the canvas binds hand-edited sample data. */
const SAMPLE_NOTICE =
  "The canvas is showing editable sample data. Open Sample to change it. Saved templates never contain this data.";

interface QuoteTemplateEditorProps {
  /** The template being edited. */
  templateId: string;
  /** The template's name, shown in the header. */
  templateName: string;
  /** The template's stored document, seeding the editor. */
  seedDocument: string;
  /** Signed asset urls keyed by asset id, including local upload previews. */
  assetUrls: DocumentAssetUrls;
  /** Registers an uploaded asset id and file so its preview outlives the editor. */
  onLocalAsset: (assetId: string, file: File) => void;
  /** Called when the unsaved-changes state flips. */
  onDirtyChange: (dirty: boolean) => void;
  /** Called when the user asks to close the editor. */
  onRequestClose: () => void;
}

/**
 * The template editor: the same three-pane canvas as the per-quote editor,
 * bound to whichever stand-in data the author picked, because a template
 * belongs to the organization rather than to any one quote. The binding is a
 * genuine `PublicQuote` either way, so the canvas renders through exactly the
 * pipeline the customer page uses.
 *
 * Unlike the per-quote editor this saves explicitly rather than autosaving. A
 * template is org-wide configuration that every new quote seeds from, so an
 * accidental keystroke should not reach it; there is also no per-quote
 * optimistic-concurrency version to coordinate, so nothing needs a debounce.
 * @param templateId - The template being edited
 * @param templateName - The template's name, shown in the header
 * @param seedDocument - The template's stored document, seeding the editor
 * @param assetUrls - Signed asset urls keyed by asset id
 * @param onLocalAsset - Registers an uploaded asset id and file
 * @param onDirtyChange - Called when the unsaved-changes state flips
 * @param onRequestClose - Called when the user asks to close the editor
 * @returns The template editor element
 */
export function QuoteTemplateEditor({
  templateId,
  templateName,
  seedDocument,
  assetUrls,
  onLocalAsset,
  onDirtyChange,
  onRequestClose,
}: QuoteTemplateEditorProps) {
  const updateTemplate = useQuoteDocumentTemplatesStore(
    (state) => state.updateTemplate,
  );
  const binding = useTemplateSampleStore((state) =>
    selectBinding(state.bindings, templateId),
  );
  const sample = useTemplateSampleStore((state) =>
    selectSample(state.samples, templateId),
  );
  const boundQuoteId = binding.kind === "quote" ? binding.quoteId : null;
  const pickedQuote = useRecordQuotesStore((state) =>
    boundQuoteId ? state.quotes.get(boundQuoteId) : undefined,
  );
  const pickedProjection = useRecordQuotesStore((state) =>
    boundQuoteId ? state.publicQuotes.get(boundQuoteId) : undefined,
  );
  const configured = useConfiguredSampleValues();
  const orgLogoUrl = useOrganizationStore(
    (state) => state.organization?.logoUrl ?? null,
  );

  // A picked quote's projection arrives asynchronously; until it does the
  // canvas keeps whatever it was already showing rather than blanking out.
  const lastBoundRef = useRef<PublicQuote>(PLACEHOLDER_QUOTE);
  const boundQuote = useMemo(() => {
    if (binding.kind === "placeholders") {
      return PLACEHOLDER_QUOTE;
    }
    if (binding.kind === "sample") {
      return buildSampleQuote(sample, configured);
    }
    // The canvas designs the layout, not the quote, so a picked quote's DRAFT/TEST mark stays off it.
    return pickedProjection
      ? { ...pickedProjection, watermark: null }
      : lastBoundRef.current;
  }, [binding.kind, sample, configured, pickedProjection]);
  lastBoundRef.current = boundQuote;

  const [name, setName] = useState(templateName);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const editor = useDocumentEditor(boundQuote, seedDocument, isUploadingImage);
  const [isPreview, setIsPreview] = useState(false);
  const [isSampleSheetOpen, setIsSampleSheetOpen] = useState(false);
  const [savedHtml, setSavedHtml] = useState(seedDocument);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isDirty = editor.documentHtml !== savedHtml;
  const quoteLabel = describeQuote(pickedQuote);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Esc leaves the customer view, but the open sheet owns Esc first: one press
  // should dismiss the sheet, not dismiss it and drop the customer view too.
  useEffect(() => {
    if (!isPreview || isSampleSheetOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPreview(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPreview, isSampleSheetOpen]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) {
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const save = async () => {
    const pending = editor.documentHtml;
    setIsSaving(true);
    setSaveError(null);
    const error = await updateTemplate(templateId, { document: pending });
    setIsSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    setSavedHtml(pending);
    toast.success(`"${name}" saved.`);
  };

  // The sheet owns the customer-view switch, so it stays mounted across both
  // views - unmounting it would strand the author in the customer view with no
  // way back except Esc.
  const sampleSheet = (
    <SampleDataSheet
      templateId={templateId}
      documentHtml={editor.documentHtml}
      open={isSampleSheetOpen}
      onOpenChange={setIsSampleSheetOpen}
      isCustomerView={isPreview}
      onCustomerViewChange={setIsPreview}
    />
  );

  if (isPreview) {
    return (
      <>
        <DocumentPreviewScreen
          documentHtml={editor.renderedHtml}
          quote={boundQuote}
          assetUrls={assetUrls}
          orgLogoUrl={orgLogoUrl}
          hint={previewHint(binding, quoteLabel)}
          exitLabel="Back to editing"
          onExitPreview={() => setIsPreview(false)}
          onRequestClose={onRequestClose}
        />
        {sampleSheet}
      </>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-3 border-b bg-background px-4 py-2 shrink-0">
        <BackButton onClick={onRequestClose} />
        <TemplateNameField
          templateId={templateId}
          name={name}
          onRenamed={setName}
        />
        <DocumentEditorToolbar editor={editor} />
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSampleSheetOpen(true)}
          >
            <FlaskConical className="size-4" />
            {sampleButtonLabel(binding, quoteLabel)}
          </Button>
          <Button size="sm" disabled={!isDirty || isSaving} onClick={save}>
            Save template
            {isSaving && <Loader2 className="size-4 animate-spin" />}
          </Button>
        </div>
      </header>
      <p className="border-b bg-muted/50 px-4 py-1.5 text-xs text-muted-foreground">
        {bindingNotice(binding, quoteLabel)}
      </p>
      {saveError && (
        <p className="border-b bg-destructive/10 px-4 py-1.5 text-xs text-destructive">
          {saveError}
        </p>
      )}
      <DocumentEditorPanes
        editor={editor}
        quote={boundQuote}
        assetUrls={assetUrls}
        orgLogoUrl={orgLogoUrl}
        onLocalAsset={onLocalAsset}
        isUploadingImage={isUploadingImage}
        onUploadingChange={setIsUploadingImage}
      />
      {sampleSheet}
    </div>
  );
}

/**
 * How a picked quote is named in the header and banner: its display number and
 * revision, so the author can tell which quote the canvas borrowed.
 * @param quote - The picked quote, or undefined when none is loaded
 * @returns The quote's display label, or null when no quote is picked
 */
function describeQuote(quote: QuoteResponse | undefined): string | null {
  if (!quote) {
    return null;
  }
  return quote.revision > 0
    ? `${quote.quoteDisplayNumber} Rev ${quote.revision}`
    : quote.quoteDisplayNumber;
}

/**
 * The Sample button's label, naming the active binding so the author is never
 * surprised that the canvas is showing fake or borrowed data.
 * @param binding - The template's active binding
 * @param quoteLabel - The picked quote's display label, or null
 * @returns The button text
 */
function sampleButtonLabel(
  binding: SampleBinding,
  quoteLabel: string | null,
): string {
  if (binding.kind === "sample") {
    return "Sample: edited";
  }
  if (binding.kind === "quote" && quoteLabel) {
    return `Sample: ${quoteLabel}`;
  }
  return "Sample";
}

/**
 * The banner under the header, explaining what the canvas is currently bound to.
 * @param binding - The template's active binding
 * @param quoteLabel - The picked quote's display label, or null
 * @returns The banner text
 */
function bindingNotice(
  binding: SampleBinding,
  quoteLabel: string | null,
): string {
  if (binding.kind === "sample") {
    return SAMPLE_NOTICE;
  }
  if (binding.kind === "quote" && quoteLabel) {
    return `The canvas is previewing this template with data from quote ${quoteLabel}. Nothing from that quote is saved into the template.`;
  }
  return PLACEHOLDER_NOTICE;
}

/**
 * The hint beside the preview screen's back button, matching the banner.
 * @param binding - The template's active binding
 * @param quoteLabel - The picked quote's display label, or null
 * @returns The preview hint text
 */
function previewHint(
  binding: SampleBinding,
  quoteLabel: string | null,
): string {
  if (binding.kind === "sample") {
    return "customer view with sample data · Esc returns to Edit";
  }
  if (binding.kind === "quote" && quoteLabel) {
    return `customer view with data from ${quoteLabel} · Esc returns to Edit`;
  }
  return "customer view with placeholder values · Esc returns to Edit";
}
